import { Fragment, useCallback, useEffect, useMemo, useState, type DragEvent } from 'react'
import { API } from './api'
import { PRODUCT_DRAG_TYPE, moveId, useDragSort } from './dragSort'

/** Drag typ pre presúvanie kategórií stromom (zmena poradia aj zaradenia pod/z podkategórie). */
const CATEGORY_DRAG_TYPE = 'application/x-emili-category'

export type ShopCategory = {
  id: string
  name: string
  description: string | null
  parentId: string | null
  sortOrder: number
  visible: boolean
  productCount: number
  childCount: number
  effectiveVisible: boolean
}
type ProductLite = { id: string; name: string; category: string; priceCents: number; active: boolean; eshopVisible?: boolean; allergens?: string | null; plu?: string | null; shopCategories?: Array<{ categoryId: string }> }
type Detail = { name: string; description: string; parentId: string; visible: boolean }

const euro = (cents: number) => (cents / 100).toLocaleString('sk-SK', { style: 'currency', currency: 'EUR' })
const norm = (value: string) => value.toLocaleLowerCase('sk').normalize('NFD').replace(/\p{Diacritic}/gu, '')

export async function adminRequest(path: string, init: RequestInit = {}) {
  const headers = new Headers(init.headers)
  if (init.body) headers.set('Content-Type', 'application/json')
  const response = await fetch(`${API}${path}`, { ...init, headers, credentials: 'include' })
  if (response.status === 401) window.dispatchEvent(new Event('emili-admin-session-expired'))
  const data = await response.json().catch(() => null)
  if (!response.ok) throw new Error((data && typeof data.error === 'string' && data.error) || `Požiadavka zlyhala (HTTP ${response.status}).`)
  return data
}

export default function EshopCategories({ onProductsChanged, groupLabel = (key: string) => key }: { onProductsChanged?: () => void; groupLabel?: (key: string) => string }) {
  const [categories, setCategories] = useState<ShopCategory[]>([])
  const [products, setProducts] = useState<ProductLite[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<Detail | null>(null)
  // Poradie produktov vo vybranej kategórii (tak ich uvidí zákazník).
  const [assignedOrder, setAssignedOrder] = useState<string[]>([])
  const assigned = useMemo(() => new Set(assignedOrder), [assignedOrder])
  const setAssigned = (ids: unknown) => setAssignedOrder(Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : [])
  const [newRootName, setNewRootName] = useState('')
  const [newChildName, setNewChildName] = useState('')
  const [search, setSearch] = useState('')
  const [sourceFilter, setSourceFilter] = useState('')
  const [onlyAssigned, setOnlyAssigned] = useState(false)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const selected = categories.find(category => category.id === selectedId) ?? null
  const roots = categories.filter(category => category.parentId === null)
  const childrenOf = (id: string) => categories.filter(category => category.parentId === id)

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const [categoryData, productData] = await Promise.all([adminRequest('/api/admin/shop-categories'), adminRequest('/api/products')])
      setCategories(Array.isArray(categoryData?.categories) ? categoryData.categories : [])
      setProducts(Array.isArray(productData) ? productData : [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kategórie sa nepodarilo načítať.')
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => { void load() }, [load])

  // Po výbere kategórie načítaj jej detail a priradené produkty.
  useEffect(() => {
    if (!selected) { setDetail(null); setAssigned([]); return }
    setDetail({ name: selected.name, description: selected.description ?? '', parentId: selected.parentId ?? '', visible: selected.visible })
    let alive = true
    adminRequest(`/api/admin/shop-categories/${encodeURIComponent(selected.id)}/products`)
      .then(data => { if (alive) setAssigned(data?.productIds) })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : 'Produkty kategórie sa nepodarilo načítať.') })
    return () => { alive = false }
    // Detail sa má obnoviť iba pri zmene výberu, nie pri každom prepočte počtov.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId])

  async function run(action: () => Promise<unknown>, success?: string) {
    setBusy(true); setError(''); setNotice('')
    try {
      await action()
      if (success) setNotice(success)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Akcia sa nepodarila.')
    } finally {
      setBusy(false)
    }
  }
  const applyTree = (data: { categories?: ShopCategory[] }) => { if (Array.isArray(data?.categories)) setCategories(data.categories) }

  const createCategory = (name: string, parentId: string | null) => run(async () => {
    const data = await adminRequest('/api/admin/shop-categories', { method: 'POST', body: JSON.stringify({ name, parentId }) })
    applyTree(data)
    if (parentId) setNewChildName(''); else setNewRootName('')
    setSelectedId(data.id)
  }, `Kategória „${name.trim()}“ je vytvorená.`)

  const saveDetail = () => selected && detail && run(async () => {
    applyTree(await adminRequest(`/api/admin/shop-categories/${encodeURIComponent(selected.id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ name: detail.name, description: detail.description.trim() || null, parentId: detail.parentId || null, visible: detail.visible }),
    }))
  }, 'Zmeny sú uložené.')

  const toggleVisible = (category: ShopCategory) => run(async () => {
    applyTree(await adminRequest(`/api/admin/shop-categories/${encodeURIComponent(category.id)}`, { method: 'PATCH', body: JSON.stringify({ visible: !category.visible }) }))
    if (category.id === selectedId) setDetail(current => current && { ...current, visible: !category.visible })
  })


  /**
   * Presun kategórie potiahnutím: pustenie na horný/dolný okraj cieľa = zaradenie vedľa neho (na tú istú úroveň,
   * teda aj z podkategórie môže vzniknúť hlavná kategória a naopak), pustenie do stredu hlavnej kategórie =
   * zaradenie dovnútra ako jej podkategória. Povolené sú iba dve úrovne, preto sa dá vnoriť iba do hlavnej
   * kategórie a iba kategória bez vlastných podkategórií (tie by inak skončili v tretej úrovni).
   */
  const moveCategory = (dragId: string, targetId: string, intent: 'before' | 'after' | 'inside') => {
    const dragged = categories.find(c => c.id === dragId)
    const target = categories.find(c => c.id === targetId)
    if (!dragged || !target || dragId === targetId) return
    const newParentId = intent === 'inside' ? target.id : (target.parentId ?? null)
    const reparenting = newParentId !== (dragged.parentId ?? null)
    void run(async () => {
      let cats = categories
      if (reparenting) {
        const data = await adminRequest(`/api/admin/shop-categories/${encodeURIComponent(dragId)}`, { method: 'PATCH', body: JSON.stringify({ parentId: newParentId }) })
        applyTree(data)
        cats = Array.isArray(data?.categories) ? data.categories : cats
      }
      if (intent !== 'inside') {
        const siblings = cats.filter(c => (c.parentId ?? null) === newParentId).map(c => c.id)
        const ordered = moveId(siblings, dragId, targetId)
        if (ordered.join() !== siblings.join()) applyTree(await adminRequest('/api/admin/shop-categories/reorder', { method: 'POST', body: JSON.stringify({ ids: ordered }) }))
      }
    })
  }
  const [dragCategoryId, setDragCategoryId] = useState<string | null>(null)
  const [dropHint, setDropHint] = useState<{ id: string; intent: 'before' | 'after' | 'inside' } | null>(null)
  const categoryDragProps = (category: ShopCategory) => ({
    draggable: !busy,
    'data-dragging': dragCategoryId === category.id ? 'true' : undefined,
    'data-drop-target': dropHint?.id === category.id ? dropHint.intent : undefined,
    onDragStart: (event: DragEvent) => {
      event.stopPropagation()
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData(CATEGORY_DRAG_TYPE, category.id)
      event.dataTransfer.setData('text/plain', '')
      setDragCategoryId(category.id)
    },
    onDragOver: (event: DragEvent) => {
      if (!dragCategoryId || dragCategoryId === category.id || !event.dataTransfer.types.includes(CATEGORY_DRAG_TYPE)) return
      const dragged = categories.find(c => c.id === dragCategoryId)
      // kategória s vlastnými podkategóriami nesmie skončiť pod inou kategóriou (vznikla by 3. úroveň) –
      // na pozíciu v zozname podkategórií sa teda vôbec nedá pustiť.
      if (dragged && dragged.childCount > 0 && category.parentId !== null) return
      event.preventDefault()
      event.stopPropagation()
      event.dataTransfer.dropEffect = 'move'
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
      const ratio = (event.clientY - rect.top) / rect.height
      const canNest = category.parentId === null && (!dragged || dragged.childCount === 0)
      const intent: 'before' | 'after' | 'inside' = canNest && ratio > 0.25 && ratio < 0.75 ? 'inside' : ratio < 0.5 ? 'before' : 'after'
      if (dropHint?.id !== category.id || dropHint.intent !== intent) setDropHint({ id: category.id, intent })
    },
    onDragLeave: (event: DragEvent) => {
      if (dropHint?.id === category.id && !(event.currentTarget as Node).contains(event.relatedTarget as Node | null)) setDropHint(null)
    },
    onDrop: (event: DragEvent) => {
      if (!dragCategoryId || !event.dataTransfer.types.includes(CATEGORY_DRAG_TYPE) || !dropHint || dropHint.id !== category.id) return
      event.preventDefault()
      event.stopPropagation()
      const dragId = dragCategoryId; const intent = dropHint.intent
      setDragCategoryId(null); setDropHint(null)
      moveCategory(dragId, category.id, intent)
    },
    onDragEnd: () => { setDragCategoryId(null); setDropHint(null) },
  })

  // Pretiahnutie produktu zo zoznamu vpravo na kategóriu v strome = priradenie.
  const [productDropTarget, setProductDropTarget] = useState<string | null>(null)
  const assignTo = (categoryId: string, productId: string) => run(async () => {
    const data = await adminRequest(`/api/admin/shop-categories/${encodeURIComponent(categoryId)}/products`, { method: 'PUT', body: JSON.stringify({ add: [productId], remove: [] }) })
    if (categoryId === selectedId) setAssigned(data?.productIds)
    applyTree(data)
    await refreshProducts()
  }, `Produkt „${products.find(p => p.id === productId)?.name ?? ''}“ je pridaný do kategórie „${categories.find(c => c.id === categoryId)?.name ?? ''}“.`)
  const productDropProps = (categoryId: string) => ({
    onDragOver: (event: DragEvent) => {
      if (!event.dataTransfer.types.includes(PRODUCT_DRAG_TYPE)) return
      event.preventDefault()
      event.dataTransfer.dropEffect = 'copy'
      if (productDropTarget !== categoryId) setProductDropTarget(categoryId)
    },
    onDragLeave: (event: DragEvent) => {
      if (productDropTarget === categoryId && !(event.currentTarget as Node).contains(event.relatedTarget as Node | null)) setProductDropTarget(null)
    },
    onDrop: (event: DragEvent) => {
      if (!event.dataTransfer.types.includes(PRODUCT_DRAG_TYPE)) return
      event.preventDefault()
      setProductDropTarget(null)
      const productId = event.dataTransfer.getData(PRODUCT_DRAG_TYPE)
      if (productId) void assignTo(categoryId, productId)
    },
  })

  const remove = (category: ShopCategory) => {
    const message = category.productCount
      ? `Vymazať kategóriu „${category.name}“? ${category.productCount} produktov z nej bude vyradených (produkty samotné zostanú).`
      : `Vymazať kategóriu „${category.name}“?`
    if (!window.confirm(message)) return
    void run(async () => {
      applyTree(await adminRequest(`/api/admin/shop-categories/${encodeURIComponent(category.id)}`, { method: 'DELETE' }))
      if (selectedId === category.id) setSelectedId(null)
      await refreshProducts()
    }, 'Kategória je vymazaná.')
  }

  async function refreshProducts() {
    const productData = await adminRequest('/api/products')
    setProducts(Array.isArray(productData) ? productData : [])
    onProductsChanged?.()
  }

  const assign = (add: string[], removeIds: string[]) => selected && (add.length || removeIds.length) && run(async () => {
    const data = await adminRequest(`/api/admin/shop-categories/${encodeURIComponent(selected.id)}/products`, { method: 'PUT', body: JSON.stringify({ add, remove: removeIds }) })
    setAssigned(data?.productIds)
    applyTree(data)
    await refreshProducts()
  })

  const activeProducts = useMemo(() => products.filter(product => product.active), [products])
  const sources = useMemo(() => [...new Set(activeProducts.map(product => product.category))].sort((a, b) => a.localeCompare(b, 'sk', { numeric: true })), [activeProducts])
  const uncategorized = activeProducts.filter(product => product.eshopVisible !== false && !(product.shopCategories ?? []).length)
  const filteredProducts = useMemo(() => {
    const q = norm(search.trim())
    return activeProducts.filter(product =>
      (sourceFilter === '__none__' ? !(product.shopCategories ?? []).length : !sourceFilter || product.category === sourceFilter)
      && (!onlyAssigned || assigned.has(product.id))
      && (!q || norm(`${product.name} ${product.plu ?? ''}`).includes(q)),
    ).sort((a, b) => a.name.localeCompare(b.name, 'sk'))
  }, [activeProducts, search, sourceFilter, onlyAssigned, assigned])

  const assignedProducts = useMemo(() => {
    const byId = new Map(products.map(product => [product.id, product]))
    return assignedOrder.map(id => byId.get(id)).filter((product): product is ProductLite => Boolean(product))
  }, [products, assignedOrder])

  const alphabetical = useMemo(() => [...assignedProducts].sort((a, b) => a.name.localeCompare(b.name, 'sk', { numeric: true, sensitivity: 'base' })).map(p => p.id), [assignedProducts])
  const isAlphabetical = alphabetical.join() === assignedProducts.map(p => p.id).join()
  const sortAlphabetically = () => {
    if (!selected || !window.confirm(`Zoradiť produkty v kategórii „${selected.name}“ podľa abecedy? Vaše vlastné poradie sa prepíše.`)) return
    void saveProductOrder(alphabetical)
  }
  const assignedDrag = useDragSort('category-products', assignedOrder, ids => void saveProductOrder(ids), { disabled: busy })
  const saveProductOrder = (ids: string[]) => selected && run(async () => {
    setAssignedOrder(ids)
    try {
      const data = await adminRequest(`/api/admin/shop-categories/${encodeURIComponent(selected.id)}/products/order`, { method: 'POST', body: JSON.stringify({ productIds: ids }) })
      setAssigned(data?.productIds)
    } catch (e) {
      const data = await adminRequest(`/api/admin/shop-categories/${encodeURIComponent(selected.id)}/products`).catch(() => null)
      if (data) setAssigned(data.productIds)
      throw e
    }
  })

  const categoryName = (id: string) => {
    const category = categories.find(c => c.id === id)
    if (!category) return ''
    const parent = category.parentId ? categories.find(c => c.id === category.parentId) : null
    return parent ? `${parent.name} › ${category.name}` : category.name
  }

  const renderNode = (category: ShopCategory, isChild: boolean) => {
    const isSelected = selectedId === category.id
    const catProps = categoryDragProps(category)
    const dropProps = productDropProps(category.id)
    return <Fragment key={category.id}><div className={`cat-node ${isChild ? 'is-child' : ''} ${selectedId === category.id ? 'is-selected' : ''} ${category.effectiveVisible ? '' : 'is-muted'} ${productDropTarget === category.id ? 'is-product-drop' : ''}`}
      {...catProps}
      onDragOver={e => { dropProps.onDragOver(e); catProps.onDragOver(e) }}
      onDragLeave={e => { dropProps.onDragLeave(e); catProps.onDragLeave(e) }}
      onDrop={e => { dropProps.onDrop(e); catProps.onDrop(e) }}>
      <span className="drag-handle" aria-hidden="true" title="Potiahni a presuň">⋮⋮</span>
      <button type="button" className="cat-node-name" onClick={() => setSelectedId(category.id)}>
        <strong>{category.name}</strong>
        <small>{category.productCount} produktov{!isChild && category.childCount ? ` · ${category.childCount} podkategórií` : ''}{!category.visible ? ' · skrytá' : !category.effectiveVisible ? ' · skrytá cez hlavnú kategóriu' : ''}</small>
      </button>
      <div className="cat-node-actions">
        <button type="button" title={category.visible ? 'Skryť v e-shope' : 'Zobraziť v e-shope'} disabled={busy} onClick={() => void toggleVisible(category)}>{category.visible ? '◉' : '○'}</button>
        <button type="button" title="Vymazať" aria-label={`Vymazať ${category.name}`} className="danger" disabled={busy} onClick={() => remove(category)}>✕</button>
      </div>
    </div>
    {isSelected && <div className={`cat-assigned ${isChild ? 'is-child' : ''}`} aria-label={`Produkty v kategórii ${category.name}`}>
      {assignedProducts.length ? <ul>
        {assignedProducts.map((product, index) => <li key={product.id} className={product.active ? '' : 'is-inactive'} {...assignedDrag.itemProps(product.id)}>
          <span className="drag-handle" aria-hidden="true">⋮⋮</span>
          <span className="cat-assigned-pos">{index + 1}.</span>
          <span className="cat-assigned-name">{product.name}{!product.active && <small> · nedostupný</small>}</span>
          <span className="cat-assigned-actions">
            <button type="button" className="is-remove" title="Odobrať z kategórie" aria-label={`Odobrať ${product.name} z kategórie`} disabled={busy} onClick={() => void assign([], [product.id])}>✕</button>
          </span>
        </li>)}
      </ul> : <p>Zatiaľ žiadne produkty – zaškrtni ich vpravo.</p>}
      {assignedProducts.length > 1 && <div className="cat-assigned-foot">
        <p className="cat-assigned-hint">Poradie zmeníš potiahnutím myšou – tak ich uvidia zákazníci.</p>
        <button type="button" className="text-btn" disabled={busy || isAlphabetical} onClick={sortAlphabetically}>{isAlphabetical ? 'Zoradené podľa abecedy' : 'A→Z Zoradiť podľa abecedy'}</button>
      </div>}
    </div>}
    </Fragment>
  }

  return <div className="panel shop-categories">
    <div className="panel-heading"><div><h2>Kategórie e-shopu</h2><p>Vlastné kategórie a podkategórie, nezávislé od skupín v pokladni. Jeden produkt môže byť vo viacerých kategóriách.</p></div>
      <button className="text-btn" onClick={() => void load()} disabled={loading}>↻ Obnoviť</button></div>

    {uncategorized.length > 0 && <p className="eshop-cat-warning" role="note">{uncategorized.length} produktov má zapnuté „Zobraziť v e-shope“, ale nie je v žiadnej kategórii – zákazníci ich zatiaľ nevidia. <button type="button" className="text-btn" onClick={() => { setSourceFilter('__none__'); setOnlyAssigned(false) }}>Zobraziť ich</button></p>}
    {error && <p className="error-message">{error}</p>}
    {notice && <p className="eshop-cat-notice" role="status">{notice}</p>}

    {loading ? <p className="empty-message">Načítavam kategórie…</p> : <div className="cat-layout">
      <section className="cat-tree" aria-label="Strom kategórií">
        <form className="cat-add" onSubmit={e => { e.preventDefault(); if (newRootName.trim()) void createCategory(newRootName, null) }}>
          <input placeholder="Nová hlavná kategória, napr. Torty" value={newRootName} maxLength={60} onChange={e => setNewRootName(e.target.value)} />
          <button className="primary-btn" disabled={busy || !newRootName.trim()}>＋ Pridať</button>
        </form>
        {!roots.length && <p className="empty-message">Zatiaľ nemáš žiadne kategórie. Začni napríklad: Torty, Zákusky, Pralinky a bonboniéry, Darčeky.</p>}
        {roots.length > 1 && <p className="hint cat-tree-hint">Kategóriu potiahneš myšou vyššie/nižšie – zmeníš poradie. Pustením doprostred hlavnej kategórie z nej urobíš podkategóriu, pustením na okraj z podkategórie naopak hlavnú kategóriu.</p>}
        {roots.map(root => <div key={root.id} className="cat-group">
          {renderNode(root, false)}
          {childrenOf(root.id).map(child => renderNode(child, true))}
        </div>)}
      </section>

      <section className="cat-detail" aria-label="Detail kategórie">
        {!selected || !detail ? <div className="cat-empty"><strong>Vyber kategóriu vľavo</strong><p>Upravíš jej názov, presunieš ju pod inú kategóriu, pridáš podkategórie a priradíš produkty.</p></div> : <>
          <h3>{categoryName(selected.id)}</h3>
          <div className="cat-fields">
            <label>Názov<input value={detail.name} maxLength={60} onChange={e => setDetail({ ...detail, name: e.target.value })} /></label>
            <label>Umiestnenie<select value={detail.parentId} onChange={e => setDetail({ ...detail, parentId: e.target.value })} disabled={selected.childCount > 0}>
              <option value="">Hlavná kategória</option>
              {roots.filter(root => root.id !== selected.id).map(root => <option key={root.id} value={root.id}>Podkategória v „{root.name}“</option>)}
            </select></label>
            <label className="cat-wide">Popis pre zákazníkov (nepovinný)<textarea rows={2} maxLength={500} value={detail.description} onChange={e => setDetail({ ...detail, description: e.target.value })} /></label>
            <label className="check-label"><input type="checkbox" checked={detail.visible} onChange={e => setDetail({ ...detail, visible: e.target.checked })} /> Zobraziť v e-shope</label>
            <button className="primary-btn" disabled={busy || !detail.name.trim()} onClick={() => void saveDetail()}>Uložiť kategóriu</button>
          </div>
          {selected.childCount > 0 && <p className="hint">Kategória má podkategórie, preto zostáva hlavnou kategóriou.</p>}

          {selected.parentId === null && <form className="cat-add" onSubmit={e => { e.preventDefault(); if (newChildName.trim()) void createCategory(newChildName, selected.id) }}>
            <input placeholder={`Nová podkategória v „${selected.name}“`} value={newChildName} maxLength={60} onChange={e => setNewChildName(e.target.value)} />
            <button className="primary-btn" disabled={busy || !newChildName.trim()}>＋ Podkategória</button>
          </form>}

          <div className="cat-products">
            <div className="cat-products-head">
              <strong>Produkty v kategórii: {assigned.size}</strong>
              <span className="hint">Zaškrtnutím produkt priradíš, odškrtnutím odoberieš. Produkt môžeš aj potiahnuť na ľubovoľnú kategóriu vľavo. Zmena sa uloží hneď.</span>
            </div>
            <div className="cat-products-filters">
              <input className="search-input" placeholder="Hľadať produkt alebo PLU…" value={search} onChange={e => setSearch(e.target.value)} />
              <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} aria-label="Skupina z pokladne">
                <option value="">Všetky skupiny z pokladne</option>
                <option value="__none__">Bez kategórie e-shopu</option>
                {sources.map(source => <option key={source} value={source}>{groupLabel(source)}</option>)}
              </select>
              <label className="check-label"><input type="checkbox" checked={onlyAssigned} onChange={e => setOnlyAssigned(e.target.checked)} /> Iba priradené</label>
            </div>
            <div className="cat-products-bulk">
              <span>Zobrazených: {filteredProducts.length}</span>
              <button type="button" className="text-btn" disabled={busy || !filteredProducts.some(p => !assigned.has(p.id))} onClick={() => void assign(filteredProducts.filter(p => !assigned.has(p.id)).map(p => p.id), [])}>Priradiť všetky zobrazené</button>
              <button type="button" className="text-btn" disabled={busy || !filteredProducts.some(p => assigned.has(p.id))} onClick={() => void assign([], filteredProducts.filter(p => assigned.has(p.id)).map(p => p.id))}>Odobrať všetky zobrazené</button>
            </div>
            <ul className="cat-product-list">{filteredProducts.slice(0, 400).map(product => {
              const others = (product.shopCategories ?? []).map(link => link.categoryId).filter(id => id !== selected.id).map(categoryName).filter(Boolean)
              return <li key={product.id} className={assigned.has(product.id) ? 'is-assigned' : ''} draggable={!busy}
                title="Potiahni na kategóriu vľavo a produkt sa do nej pridá"
                onDragStart={e => { e.dataTransfer.effectAllowed = 'copy'; e.dataTransfer.setData(PRODUCT_DRAG_TYPE, product.id); e.dataTransfer.setData('text/plain', product.name) }}
                onDragEnd={() => setProductDropTarget(null)}>
                <label className="check-label"><input type="checkbox" disabled={busy} checked={assigned.has(product.id)} onChange={e => void assign(e.target.checked ? [product.id] : [], e.target.checked ? [] : [product.id])} /> {product.name}</label>
                <span className="cat-product-meta">{groupLabel(product.category)}{others.length ? ` · aj v: ${others.join(', ')}` : ''}</span>
                {product.eshopVisible === false && <span className="product-status is-hidden">Mimo e-shopu</span>}
                {product.eshopVisible !== false && !product.allergens && <span className="product-status is-warning">Bez alergénov</span>}
                <span className="cat-product-price">{euro(product.priceCents)}</span>
              </li>
            })}</ul>
            {filteredProducts.length > 400 && <p className="hint">Zobrazených je prvých 400 produktov – spresni hľadanie.</p>}
          </div>
        </>}
      </section>
    </div>}
  </div>
}
